<template>
  <PageContent>
    <ProductDetailSkeleton
      v-if="fetching"
      :with-variant-box="true"
      :with-text-info-box="true"
      :with-attributes="true"
    />
    <div v-else-if="product && (parseInt(packageUnit, 10) || 0) > -1">
      <ProductDetailTwoColumns
        :product="product"
        :selected-variant="selectedVariant"
        :get-attribute="getProductAttribute(product, selectedVariant)"
        :has-siblings="hasSiblings"
        :product-detail-properties="PRODUCT_DETAIL_ATTRIBUTE_KEYS_HEARING_AIDS"
        :detail-name-mapping="detailNameMapping"
        :back-url="backUrl"
        :product-name="productName"
        :product-color="productColor"
      >
        <template #actions>
          <div class="flex flex-col gap-6">
            <div class="price-summary border-b border-gray-350 pb-6">
              <ProductPrice
                size="2xl"
                :price="price"
                no-margin
                :applied-reductions="price.appliedReductions"
                :show-price-from="hasSpecial"
                :show-price-reduction-badge="hasSpecial"
                :show-discount="true"
                data-testid="product-price"
              />

              <ProductDetailDescriptionText>
                <span class="mt-2 inline-block text-xs leading-5">
                  <template v-if="price.withTax > 0">
                    {{ $t('pdp.glasses.with_tax') + ' ' }}
                  </template>
                  <ProductShippingCosts
                    :price="price"
                    :quantity="quantity"
                    :product-id="product.id"
                    text-size="xs"
                  />
                  <span>
                    {{ ` – ${$t('pdp.delivery_location_information')}` }}
                  </span>
                  <span v-if="lowestPrice" class="flex text-gray-600">
                    {{ $t('pdp.lowest_price') }}
                    {{ `${lowestPrice}` }}
                  </span>
                </span>
              </ProductDetailDescriptionText>
            </div>

            <div v-if="hasSiblings" class="w-full">
              <Headline tag="span" look="h5">
                {{ $t('pdp.colors_heading') }}
              </Headline>
              <ProductDetailSiblingSlider
                class="mt-3 max-w-[100vw]"
                :product="product"
                :ignore-sold-out="false"
              />
            </div>
            <div
              v-if="
                product.variants && everyVariantHasDiopters(product.variants)
              "
              class="mt-5 w-full"
            >
              <ProductDetailVariantSlider
                class="mt-3 max-w-[100vw]"
                :product="product"
              />
            </div>
            <div>
              <ProductPromotionGiftConditionBanner
                v-if="!areGiftConditionsMet && isGiftAddedToBasket"
                :product="product"
              />
              <ProductPromotionBanners v-else :product="product" />
              <div class="h-fit rounded-lg bg-gray-150 px-5 py-6 lg:px-8">
                <ProductDetailDeliveryEstimates
                  :key="deliveryEstimatesKey"
                  :stock="stock"
                  :sales-status="salesStatus"
                  :icon-size="2.5"
                  :product="product"
                  class="mb-4 justify-center !font-normal"
                />

                <FimButton
                  data-testid="add-to-cart"
                  type="primary"
                  class="mb-0 w-full"
                  :disabled="isA2bDisabled"
                  :loading="isAddingToCart"
                  @click="addItemToBasket"
                >
                  {{ $t('pdp.add_label') }}
                  <template #iconAfter>
                    <LazyIconFielmannCart class="mr-2 size-6" />
                  </template>
                </FimButton>

                <ProductPromotionGifts
                  v-if="isBuyXGetYPrioritized && !isGiftAddedToBasket"
                  :product="product"
                  class="mt-6"
                />
              </div>
              <BenefitsList class="mt-6" slug="hearing-aids-merch" />
            </div>
          </div>
        </template>
        <template #manuals>
          <div v-if="manualUrl || complianceUrl">
            <Headline class="mb-6" tag="span" look="h3">
              {{ $t('pdp.manual.headline') }}
            </Headline>
            <ProductDetailManualLink
              v-if="manualUrl"
              class="mb-6"
              :url="manualUrl"
              translation-key="pdp.manual.copyHearingAidsMerch"
            />
            <ProductDetailManualLink
              v-if="complianceUrl"
              :url="complianceUrl"
              translation-key="pdp.manual.copyCompliance"
              link-translation-key="pdp.manual.linkCompliance"
            />
          </div>
        </template>
      </ProductDetailTwoColumns>

      <FimRecommendations
        v-if="recommendationRefXsell"
        :recommendation-product="product"
        :product-recommendations="recommendationRefXsell"
        :title="getRecommendationTitle('recommendationRefXsell', $i18n)"
        class="mt-12"
        location="PDP"
      >
        <FimRecommendationLinks :field-name="recommendationLinkFieldName" />
      </FimRecommendations>
    </div>
  </PageContent>
</template>

<script setup lang="ts">
import type {
  BasketItemDisplayData,
  Product,
  Variant,
} from '@scayle/storefront-api'
import { isEmpty } from 'radash'
import { DisplayDataAttributes } from '~/composables/pdp/pdpTypes'
import useUiState from '~/composables/ui/useUiState'
import { useFimBasket } from '~/composables/useFimBasket'
import { PRODUCT_DETAIL_ATTRIBUTE_KEYS_HEARING_AIDS } from '~/constants/product'
import type { RecommendationLinkFieldNames } from '~/storyblok/types/storyblok'
import { getLowestPriorPrice } from '~/utils/price'
import { isProductAvailable, isVariantAvailable } from '~/utils/product'
import { getQueryParam } from '~/utils/route'
import {
  generateBreadcrumbSchema,
  generateProductSchema,
  getBreadcrumbSchemaItems,
  getProductHead,
} from '~/utils/seo'

const { setPageState } = usePageState()
const scope = effectScope()
onScopeDispose(() => scope.stop())
const props = defineProps({
  baseProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
})

const context = useNuxtApp()
const { $i18n } = context
const currentShop = useCurrentShop()
const { data: shopCustomData } = useCustomData()
const route = useRoute()
const queryVariantId = computed(() => getQueryParam(route.query, 'variantid'))

const {
  product: detailedProduct,
  price,
  quantity,
  fetching,
  packageUnit,
  selectedVariant,
  hasSiblings,
  hasSpecial,
  salesStatus,
  trackProductView,
  inStock,
  stock,
  breadcrumbs,
  backUrl,
  manualUrl,
  complianceUrl,
  productName,
  variantsSortedByPrice,
  isSellableWithoutStock,
  setVariantId,
} = await useProductDetails(props.baseProduct, {
  params: {
    with: PRODUCT_WITH_HEARING_AIDS_MERCH,
  },
  options: { immediate: true },
})

onMounted(() => {
  trackProductView(product.value)
})

const { trackAddToBasket } = useTrackingEvents()
const product = computed(() => detailedProduct.value || props.baseProduct)

const {
  isBuyXGetYPrioritized,
  isGiftAddedToBasket,
  areGiftConditionsMet,
  highestPriorityPromotion,
} = useProductPromotions(product)

// Product recommendations
const { data: productRecommendations } = useAsyncProductRecommendations(
  [...PRODUCT_RECOMMENDATION_ATTRIBUTES],
  product.value,
)

const recommendationRefXsell = computed(
  () => productRecommendations.value?.recommendationRefXsell,
)

const isA2bDisabled = computed(() => {
  return (
    !isProductAvailable(product.value) ||
    !isVariantAvailable(product.value, selectedVariant.value)
  )
})

const head = computed(() =>
  getProductHead(product.value, selectedVariant.value, price.value, context),
)
useHead(() => head.value)

const router = useRouter()

const { addItem } = useFimBasket()

const isAddingToCart = ref(false)

const { openBasketOverlay } = useUiState()

const getCurrentVariant = () => {
  const variantId = getQueryParam(router.currentRoute.value.query, 'variantId')
  return (
    (product.value.variants ?? []).find(
      (variant) => variant.id.toString() === variantId,
    ) ?? variantsSortedByPrice.value[parseInt(packageUnit.value, 10) || 0]
  )
}

const productColor = computed(() =>
  getFirstAttributeLabel(product.value?.attributes, 'baseColor'),
)

const isConsultantUsed = computed(
  () => getQueryParam(route.query, 'consultant', '0') === '1',
)

const addItemToBasket = async (payload?: {
  product: Product
  variant: Variant
}) => {
  if (
    !isAddingToCart.value &&
    variantsSortedByPrice.value &&
    variantsSortedByPrice.value.length
  ) {
    isAddingToCart.value = true

    const variant = getCurrentVariant()

    const a2bProduct =
      payload?.product && payload?.variant ? payload.product : toValue(product)
    const a2bVariant =
      payload?.product && payload?.variant ? payload.variant : toValue(variant)

    const displayDataPayload: { displayData: BasketItemDisplayData } = {
      displayData: {
        [DisplayDataAttributes.colorAttribute]: {
          key: 'colors',
          label: $i18n.t('pdp.colors_heading'),
          value: getColors(a2bProduct),
        },
      },
    }

    if (
      a2bVariant.stock?.quantity === 0 &&
      a2bVariant.stock?.isSellableWithoutStock
    ) {
      displayDataPayload.displayData[
        DisplayDataAttributes.deliveryForecastAttribute
      ] = {
        key: $i18n.t('basket.displayData.deliveryForecastFallback.key'),
        label: $i18n.t('basket.displayData.deliveryForecastFallback.label'),
        value:
          shopCustomData.value?.deliveryEstimatesProductType
            ?.sellableWithoutStockAdditionalTime ??
          $i18n.t('basket.displayData.deliveryForecastFallback.value'),
      }
    }

    const promotionId = highestPriorityPromotion.value?.id
    const branchId = getBranchIdFromSession()

    const customData = {
      ...(branchId && { branchId }),
      ...(isConsultantUsed.value && { consultantUsed: true }),
      designerOrder: isDesignerProduct(product),
      productType: getProductType(product.value),
    } as FimBasketItemCustomData

    const addItemPayload = {
      variantId: a2bVariant.id,
      quantity: Number(quantity.value),
      ...(isEmpty(displayDataPayload) ? {} : displayDataPayload),
      ...(isEmpty(customData) ? {} : { customData }),
      ...(!promotionId && { promotionId: null }),
      ...(promotionId && !isBuyXGetYPrioritized.value && { promotionId }),
    }

    await addItem(addItemPayload as any)
    isAddingToCart.value = false
    openBasketOverlay()
    trackAddToBasket(a2bProduct, Number(quantity.value), a2bVariant)
  }
}

const detailNameMapping = {
  modelName: $i18n.t('pdp.details.modelName'),
  ...(Boolean(product.value?.attributes?.sports) && {
    sports: $i18n.t('pdp.details.sports'),
  }),
}

const lowestPrice = computed(() =>
  getLowestPriorPrice(
    price.value,
    product.value?.lowestPriorPrice,
    Number(quantity.value),
  ),
)

const breadcrumbSchema = computed(() =>
  getBreadcrumbSchemaItems(
    breadcrumbs.value,
    currentShop.value.domain || '',
    route.fullPath,
    head.value.title || '',
  ),
)

const recommendationLinkFieldName = ref<RecommendationLinkFieldNames>(
  'hearing_aids_merch_links',
)

setPageState('pageType', 'product')

// todo: this was a hack to get around UI not updating if I remember correctly. Check if still needed
const deliveryEstimatesKey = ref(0)
scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch([stock, inStock, isSellableWithoutStock, isA2bDisabled], () => {
    deliveryEstimatesKey.value += 1
  })

  watch(
    queryVariantId,
    (next) => {
      if (next) {
        setVariantId(next)
      }
    },
    { immediate: true },
  )
})

const productSchema = computed(() =>
  generateProductSchema(
    product.value,
    selectedVariant.value,
    price.value,
    context,
  ),
)

useJsonld(() => productSchema.value)
useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))
</script>
